import React, { useEffect, useMemo } from 'react';

import currencyFormatter from 'currency-formatter';
import { Link as NavLink } from '~/lib/react-router-dom';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, Button } from '@mui/material';

import { useGetCreditReversals } from '~/hooks/apis/payments/useCreditReversals';

import type { GetCreditReversalsParams } from '~/services/PaymentsService';

const PENDING_CREDIT_REVERSAL_LIMIT = 1000;

interface PendingCreditReversalAlertProps {
  currency: string;
  customerId: string;
  refreshKey?: number;
}

function formatTotalPendingAmount(amount: number, currency: string): string {
  return `${currencyFormatter.format(amount, { code: currency, precision: 2 })} ${currency}`;
}

export default function PendingCreditReversalAlert({
  currency,
  customerId,
  refreshKey = 0,
}: PendingCreditReversalAlertProps) {
  const queryParams = useMemo<GetCreditReversalsParams>(
    () => ({
      customerId,
      status: 'pending',
      limit: PENDING_CREDIT_REVERSAL_LIMIT.toString(),
      offset: '0',
    }),
    [customerId],
  );
  const managementPagePath = `/system/credit-reversals?customerId=${encodeURIComponent(customerId)}`;
  const { data, error, isLoading, refetch } = useGetCreditReversals(queryParams);

  useEffect(() => {
    if (refreshKey > 0) {
      void refetch();
    }
  }, [refetch, refreshKey]);

  const pendingCreditReversals = data?.result ?? [];
  const pendingRequestCount = pendingCreditReversals.length;
  const pendingCurrency = pendingCreditReversals[0]?.currency ?? currency;
  const totalPendingReversalAmount = pendingCreditReversals.reduce(
    (sum, creditReversal) => sum + Number(creditReversal.cash_reversed_amount ?? 0),
    0,
  );
  const requestLabel = pendingRequestCount === 1 ? 'request' : 'requests';

  const viewMoreAction = (
    <Button
      component={NavLink}
      to={managementPagePath}
      target="_blank"
      rel="noreferrer"
      size="small"
      color="inherit"
      endIcon={<OpenInNewIcon fontSize="small" />}
    >
      View All
    </Button>
  );

  if (isLoading) {
    return <Alert severity="info">Checking pending credit reversal requests...</Alert>;
  }

  if (error) {
    return (
      <Alert severity="warning" action={viewMoreAction}>
        Failed to load pending credit reversal requests.
      </Alert>
    );
  }

  if (pendingRequestCount === 0) {
    return (
      <Alert severity="info" action={viewMoreAction}>
        There are no pending credit reversal requests.
      </Alert>
    );
  }

  return (
    <Alert severity="warning" action={viewMoreAction}>
      There are {pendingRequestCount} pending credit reversal {requestLabel} totalling{' '}
      {formatTotalPendingAmount(totalPendingReversalAmount, pendingCurrency)}.
    </Alert>
  );
}
